/**
 * DiagnosticEngine: Analyzes itemized exam results to compute section performance,
 * granular skill breakdowns (True/False/Not Given, Multiple Choice, Sentence Completion, Summary),
 * strengths, development areas, and targeted LLAP learning recommendations.
 */
export const DiagnosticEngine = {
  analyze(submission, questionMetadataMap = {}) {
    const results = submission.results || [];
    const totalQuestions = submission.maxScore || results.length || 40;
    const correctCount = submission.rawScore || results.filter(q => q.isCorrect).length;
    const incorrectCount = results.filter(q => !q.isCorrect && q.studentAnswer !== '').length;
    const unansweredCount = totalQuestions - (correctCount + incorrectCount);

    // 1. Granular Skill & Question Type Categorization Mapping
    const skillCategories = [
      { name: 'True / False / Not Given', range: [1, 5] },
      { name: 'Multiple Choice & Matching Headings', range: [6, 13] },
      { name: 'Sentence Completion', range: [14, 26] },
      { name: 'Summary & Feature Matching', range: [27, 40] }
    ];

    const skillMap = {};
    skillCategories.forEach(cat => {
      skillMap[cat.name] = { total: 0, correct: 0 };
    });

    results.forEach((q, idx) => {
      const qNum = idx + 1;
      let assignedSkill = 'General Reading Comprehension';
      skillCategories.forEach(cat => {
        if (qNum >= cat.range[0] && qNum <= cat.range[1]) {
          assignedSkill = cat.name;
        }
      });

      if (!skillMap[assignedSkill]) skillMap[assignedSkill] = { total: 0, correct: 0 };
      skillMap[assignedSkill].total += 1;
      if (q.isCorrect) skillMap[assignedSkill].correct += 1;
      
      // Attach inferred sub-skill to result item for downstream reporting and itemized review
      q.subSkill = assignedSkill;
    });

    const skillBreakdown = Object.keys(skillMap).map(skill => {
      const stats = skillMap[skill];
      const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
      let status = 'Developing';
      if (accuracy >= 85) status = 'Strong';
      else if (accuracy >= 70) status = 'Competent';
      return { skill, total: stats.total, correct: stats.correct, accuracy, status };
    });

    // 2. Strengths and Performance Gaps
    const strengths = skillBreakdown
      .filter(s => s.accuracy >= 70)
      .map(s => `Strong performance demonstrated in ${s.skill} (${s.accuracy}% accuracy).`);

    const developmentAreas = skillBreakdown
      .filter(s => s.accuracy < 70)
      .map(s => `Performance gap identified in ${s.skill} (${s.accuracy}% accuracy). Targeted evidence-location practice recommended.`);

    if (strengths.length === 0) strengths.push('Consistent engagement across foundational reading tasks.');
    if (developmentAreas.length === 0) developmentAreas.push('Refine pacing and time allocation across complex multi-paragraph passages.');

    // 3. Section-by-Section Breakdown (Passages 1, 2, 3)
    const section1 = results.slice(0, 13);
    const section2 = results.slice(13, 26);
    const section3 = results.slice(26, 40);

    const countCorrect = (arr) => arr.filter(q => q.isCorrect).length;
    const sectionPerformance = [
      { section: 'Section 1 (Passage 1)', correct: countCorrect(section1), total: section1.length || 13 },
      { section: 'Section 2 (Passage 2)', correct: countCorrect(section2), total: section2.length || 13 },
      { section: 'Section 3 (Passage 3)', correct: countCorrect(section3), total: section3.length || 14 }
    ];

    // 4. Actionable Recommendations linked to actual gaps
    const recommendations = developmentAreas.map((area, idx) => {
      const extractedSkill = area.split('identified in ')[1]?.split(' (')[0] || 'Core Comprehension';
      return {
        priority: idx + 1,
        focusArea: extractedSkill,
        actionableAdvice: `Complete targeted LLAP modules focusing on evidence-identification, scanning, and structured practice for ${extractedSkill}.`
      };
    });

    return {
      totalQuestions,
      correctCount,
      incorrectCount,
      unansweredCount,
      percentage: Math.round((correctCount / totalQuestions) * 100),
      skillBreakdown,
      strengths,
      developmentAreas,
      sectionPerformance,
      recommendations
    };
  }
};

export default DiagnosticEngine;